import { zoom } from 'd3';
import React, { useEffect, useState } from 'react';
import {
  ResponsiveContainer,
  ScatterChart,
  CartesianGrid,
  XAxis,
  YAxis,
  ZAxis,
  ReferenceArea,
  Scatter,
  Tooltip,
  Label,
  ReferenceLine,
  Cell,
} from 'recharts';
import { useData } from '../../contexts';
import { CustomDot } from './CustomDot';
import getClickedPoint from './getClickedPoint';

export const ScatterChartQuadrants = (props: any) => {
  const {
    pageId,
    xAxisDataKey,
    yAxisDataKey,
    zAxisDataKey,
    incomingData,
    zoomFilters,
    resetZoom,
    setResetZoom,
    setDataTable,
    setZoomFilters,
    setIsZoomedInsights,
    zoomGraph,
    setZoomGraph,
    locale,
    highlightedRow,
    selectedPeriod,
    tableHighlightedRow,
    setTableHighlightedRow,
    setHighlightedRow,
  } = props;

  const { selectedGemeente, selectedBenchmarks, fillColorHighlight } =
    useData();

  const DEFAULT_ZOOM = { x1: null, y1: null, x2: null, y2: null };
  const [zoomArea, setZoomArea] = useState(DEFAULT_ZOOM);
  const [isZooming, setIsZooming] = useState(false);

  const [dataGraph, setDataGraph] = useState(incomingData) as any[];
  const [valMax, setValMax] = useState(1);

  const [selectedPoint, setSelectedPoint] = useState(null);

  useEffect(() => {
    let filteredData = structuredClone(incomingData);

    for (const property in zoomFilters) {
      filteredData = filteredData.filter((d: any) => {
        return zoomFilters[property].includes(d.id);
      });
    }

    let tmpData = structuredClone(filteredData).sort(
      (a: any, b: any) =>
        // a.indicator.localeCompare(b.indicator),
        b[xAxisDataKey] - a[xAxisDataKey],
    );

    const maxGemeente = Math.max(
      ...tmpData.map((o: any) => {
        let val = Math.abs(o[yAxisDataKey]);
        if (isNaN(val) || val == Infinity) {
          val = 0;
        }
        return val;
      }),
    );
    const minGemeente = Math.min(
      ...tmpData.map((o: any) => {
        let val = Math.abs(o[yAxisDataKey]);
        if (isNaN(val) || val == Infinity) {
          val = 0;
        }
        return val;
      }),
    );
    const maxBench = Math.max(
      ...tmpData.map((o: any) => {
        let val = Math.abs(o[xAxisDataKey]);
        if (isNaN(val) || val == Infinity) {
          val = 0;
        }
        return val;
      }),
    );
    const minBench = Math.min(
      ...tmpData.map((o: any) => {
        let val = Math.abs(o[xAxisDataKey]);
        if (isNaN(val) || val == Infinity) {
          val = 0;
        }
        return val;
      }),
    );

    const absMax = Math.max(minGemeente, minBench, maxGemeente, maxBench) * 1.1;

    setValMax(absMax);

    const theRow = filteredData.find((d: any) => d.id === highlightedRow);
    const dataWithoutTheRow = filteredData.filter(
      (d: any) => d.id !== highlightedRow,
    );

    const visualData =
      theRow !== undefined
        ? dataWithoutTheRow.concat([theRow])
        : dataWithoutTheRow;

    setDataGraph(visualData);
    setDataTable(tmpData);
    // }, [zoomFilters, incomingData, highlightedRow]);
  }, [zoomFilters, incomingData]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      setHighlightedRow(null);
      const filteredData = dataGraph.filter((d: any) => {
        return d[xAxisDataKey] === payload[0].value;
      });

      let formattedTick =
        payload[0]?.payload[yAxisDataKey] !== null
          ? locale.format(',.1%')(payload[0]?.payload[xAxisDataKey])
          : '-';
      let sign = payload[0]?.payload[xAxisDataKey] > 0 ? '+' : '';
      let newValue = sign + formattedTick;

      let formattedTickBench =
        payload[0]?.payload[yAxisDataKey] !== null
          ? locale.format(',.1%')(payload[0]?.payload[yAxisDataKey])
          : '-';
      let signBench = payload[0]?.payload[yAxisDataKey] > 0 ? '+' : '';
      let newValueBench = signBench + formattedTickBench;

      return (
        <div
          style={{
            backgroundColor: 'white',
            padding: 8,
            border: '1px solid #aaa',
            maxWidth: '400px',
          }}
        >
          <ul>
            <li>
              Relatieve toe-/afname {selectedGemeente.label}: {newValue}
            </li>
            <li>
              Relatieve toe-/afname {selectedBenchmarks[0].label}:{' '}
              {newValueBench}
            </li>
            <li>
              <p style={{ marginBottom: 8 }}>Indicatoren:</p>
              <ul>
                {filteredData.map((d: any) => {
                  return <li>{d?.indicator}</li>;
                })}
              </ul>
            </li>
          </ul>
        </div>
      );
    }
  };

  const CustomReferenceArea1 = (props) => {
    return (
      <polygon
        points={`${props.x}, ${props.y + props.height} ${
          props.x + props.width
        },${props.y} ${props.x},${props.y}`}
        style={{
          fill: '#F3E9EB',
          fillOpacity: 1,
          stroke: '#666',
          strokeOpacity: 0,
          strokeWidth: 1,
        }}
      />
    );
  };

  const CustomReferenceArea2 = (props) => {
    return (
      <polygon
        points={`${props.x}, ${props.y + props.height} ${
          props.x + props.width
        },${props.y + props.height} ${props.x + props.width},${props.y}`}
        style={{
          fill: '#E4F8F2',
          fillOpacity: 1,
          stroke: '#666',
          strokeOpacity: 0,
          strokeWidth: 1,
        }}
      />
    );
  };

  const handleMouseEnter = (event) => {
    // Access the data point ID from the event object
    const id = event.target.id;
    setTableHighlightedRow(id);
  };

  const handleMouseOut = (event) => {
    setHighlightedRow(null);
    // setTableHighlightedRow(null);
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ScatterChart
        margin={{ top: 72, right: 24, left: 0, bottom: 24 }}
        data={dataGraph}
        onMouseDown={(e) => {
          setIsZooming(true);
          const { chartX, chartY, xValue, yValue } = e || {};
          const clickedPoint = getClickedPoint(chartX, chartY, dataGraph);
          // console.log(clickedPoint, 'clickedPoint');
          setZoomArea({ x1: xValue, y1: yValue, x2: xValue, y2: yValue });
        }}
        onMouseMove={(e) => {
          if (isZooming) {
            // console.log("zoom selecting");
            setZoomArea((prev: any) => ({
              ...prev,
              x2: e?.xValue,
              y2: e?.yValue,
            }));
          }
        }}
        onMouseUp={() => {
          if (isZooming) {
            setIsZooming(false);
            let { x1, y1, x2, y2 } = zoomArea;

            // ensure x1 <= x2 and y1 <= y2
            if (x1 > x2) [x1, x2] = [x2, x1];
            if (y1 > y2) [y1, y2] = [y2, y1];

            // console.log("zoom stop");
            const dataPointsInRange = incomingData.filter(
              (d: any) =>
                d[xAxisDataKey] >= x1 &&
                d[xAxisDataKey] <= x2 &&
                d[yAxisDataKey] >= y1 &&
                d[yAxisDataKey] <= y2,
            );

            if (dataPointsInRange.length === 0) return;

            setDataGraph(dataPointsInRange);
            setDataTable(dataPointsInRange);
            const unique = [
              ...new Set(dataPointsInRange.map((d: any) => d.id)),
            ];

            setZoomFilters((prev: any) => ({ ...prev, [pageId]: unique }));
            setZoomArea(DEFAULT_ZOOM);
          }
        }}
        onMouseLeave={(e: any) => {
          setZoomArea(DEFAULT_ZOOM);
          setIsZooming(false);
        }}
      >
        <ReferenceArea shape={<CustomReferenceArea1 />} />

        <ReferenceArea shape={<CustomReferenceArea2 />} />
        <CartesianGrid strokeDasharray="0 0" />
        <XAxis
          allowDataOverflow={true}
          dataKey={xAxisDataKey}
          domain={[-valMax, valMax]}
          type="number"
          tickFormatter={(tick) => {
            let formattedTick =
              tick !== null ? locale.format(',.1%')(tick) : '-';
            let sign = tick > 0 ? '+' : '';
            return sign + formattedTick;
          }}
          tickMargin={12}
          ticks={[-valMax, 0, valMax]}
          strokeOpacity={0}
        />
        <YAxis
          allowDataOverflow
          dataKey={yAxisDataKey}
          domain={[-valMax, valMax]}
          type="number"
          width={80}
          tickMargin={12}
          ticks={[-valMax, 0, valMax]}
          strokeOpacity={0}
          tickFormatter={(tick) => {
            let formattedTick =
              tick !== null ? locale.format(',.1%')(tick) : '-';
            let sign = tick > 0 ? '+' : '';
            return sign + formattedTick;
          }}
        />
        <ZAxis range={[0, 300]} dataKey={zAxisDataKey} />

        {isZooming && (
          <ReferenceArea
            x1={zoomArea?.x1}
            x2={zoomArea?.x2}
            y1={zoomArea?.y1}
            y2={zoomArea?.y2}
          />
        )}

        <ReferenceLine
          x={0}
          stroke="#666"
          label={
            <Label
              value={'Evolutie ' + selectedBenchmarks[0].ruimte}
              position="top"
            />
          }
          shape={(props) => {
            return (
              <>
                <defs>
                  <marker
                    id="arrow"
                    markerWidth="10"
                    markerHeight="10"
                    refX="10"
                    refY="3"
                    orient="auto"
                    markerUnits="strokeWidth"
                  >
                    <path d="M0,0 L0,6 L9,3 z"></path>
                  </marker>
                </defs>
                <line marker-end="url(#arrow)" {...props} y2={props.y2} />
              </>
            );
          }}
        />

        <ReferenceLine
          y={0}
          stroke="#666"
          label={
            <Label
              value={'Evolutie ' + selectedGemeente.ruimte}
              position="insideBottomRight"
            />
          }
          shape={(props) => {
            return (
              <>
                <defs>
                  <marker
                    id="arrow"
                    markerWidth="10"
                    markerHeight="10"
                    refX="0"
                    refY="3"
                    orient="auto"
                    markerUnits="strokeWidth"
                  >
                    <path d="M0,0 L0,6 L9,3 z"></path>
                  </marker>
                </defs>
                <line marker-end="url(#arrow)" {...props} y2={props.y2} />
              </>
            );
          }}
        />

        <ReferenceLine
          stroke="#666"
          strokeDasharray="3 3"
          segment={[
            { x: -valMax, y: -valMax },
            { x: valMax, y: valMax },
          ]}
        />

        <Scatter
          style={{ cursor: 'pointer' }}
          type="natural"
          dataKey={xAxisDataKey}
          animationDuration={300}
          fill="#666"
          fillOpacity={0.2}
          stroke="#666"
          strokeOpacity={0.6}
          isAnimationActive={false}
          // shape={<CustomDot selectedPoint={selectedPoint} />}
        >
          {dataGraph.map((entry, index) => {
            return (
              <Cell
                key={`cell-${entry?.id}`}
                fill={
                  entry?.id === highlightedRow ||
                  parseInt(entry?.id) === parseInt(tableHighlightedRow)
                    ? fillColorHighlight
                    : '#fff'
                }
                fillOpacity={
                  entry?.id === highlightedRow ||
                  parseInt(entry?.id) === parseInt(tableHighlightedRow)
                    ? 1
                    : 0.2
                }
                onMouseEnter={handleMouseEnter}
                onMouseOut={handleMouseOut}
              />
            );
          })}
        </Scatter>

        <Tooltip
          allowEscapeViewBox={{ x: false, y: true }}
          wrapperStyle={{ zIndex: 10 }}
          content={<CustomTooltip />}
          cursor={{ strokeDasharray: '3 3', stroke: '#666', strokeWidth: 1 }}
        />
      </ScatterChart>
    </ResponsiveContainer>
  );
};
